import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { CtChalkTag } from './chalk-tag.directive';
import {
  PRODUCER_TIERS,
  formatEurFromCents,
  nextTier,
  tierByLevel,
  tierForMonthlyRevenue,
} from '../core/domain/tiers';

/**
 * Progression du producteur vers le palier suivant — l'encart du tableau de
 * bord de la maquette.
 *
 * Le dossier décrit deux axes de progression indépendants, le palier économique
 * et les succès. Cet encart n'affiche que le premier, et affiche surtout le
 * montant qui reste à parcourir : c'est ce chiffre, et non la barre, qui donne
 * envie de revenir.
 */
@Component({
  selector: 'ct-tier-progress',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CtChalkTag],
  template: `
    <div class="head">
      <span class="title">{{ tier().title }}</span>
      <span class="rank">Palier {{ tier().level }} sur {{ total }}</span>
    </div>

    <div
      class="track"
      role="progressbar"
      aria-valuemin="0"
      aria-valuemax="100"
      [attr.aria-valuenow]="percent()"
      [attr.aria-label]="'Progression vers le palier suivant'"
    >
      <div class="fill" [style.width.%]="percent()"></div>
    </div>

    <span ctChalkTag small>{{ revenueLabel() }} ce mois</span>

    <p class="hint ct-muted">
      @if (remaining(); as gap) {
        Encore {{ gap }} avant le palier {{ tier().level + 1 }} ({{ nextTitle() }})
      } @else {
        Palier le plus élevé de la grille — bravo
      }
    </p>
  `,
  styles: `
    :host {
      display: block;
    }

    .head {
      display: flex;
      justify-content: space-between;
      align-items: baseline;
      gap: var(--ct-space-2);
      margin-bottom: var(--ct-space-2);
    }

    .title {
      font-weight: var(--ct-weight-bold);
      color: var(--ct-vert-cageot);
    }

    .rank {
      font-size: var(--ct-size-sm);
      color: var(--ct-text-muted);
      white-space: nowrap;
    }

    .track {
      height: 8px;
      background: var(--ct-kraft-dark);
      border-radius: 6px;
      overflow: hidden;
      margin-bottom: var(--ct-space-2);
    }

    .fill {
      height: 100%;
      background: var(--ct-vert-cageot);
    }

    .hint {
      font-size: var(--ct-size-xs);
      margin-top: var(--ct-space-2);
    }
  `,
})
export class CtTierProgress {
  /** Chiffre d'affaires du mois en cours, en centimes. */
  readonly monthlyRevenueCents = input.required<number>();

  /** Palier appliqué (abonnement et limites). Sans valeur, déduit du CA. */
  readonly appliedLevel = input<number | undefined>(undefined);

  protected readonly total = PRODUCER_TIERS.length;

  protected readonly tier = computed(() => {
    const applied = this.appliedLevel();
    if (applied !== undefined) {
      return tierByLevel(applied) ?? PRODUCER_TIERS[0]!;
    }
    return tierForMonthlyRevenue(this.monthlyRevenueCents());
  });

  private readonly next = computed(() => nextTier(this.tier().level));

  protected readonly nextTitle = computed(() => this.next()?.title ?? '');

  protected readonly revenueLabel = computed(() => formatEurFromCents(this.monthlyRevenueCents()));

  /**
   * Avancement à l'intérieur du palier courant.
   *
   * Au dernier palier, ouvert vers le haut, la barre est pleine : il n'y a pas
   * de borne supérieure vers laquelle progresser.
   */
  protected readonly percent = computed(() => {
    const tier = this.tier();
    if (tier.revenueToCents === undefined) {
      return 100;
    }
    const span = tier.revenueToCents - tier.revenueFromCents;
    const done = this.monthlyRevenueCents() - tier.revenueFromCents;
    return Math.min(100, Math.max(0, Math.round((done / span) * 100)));
  });

  /** Montant restant avant le palier suivant, ou `undefined` au sommet. */
  protected readonly remaining = computed(() => {
    const next = this.next();
    if (!next) {
      return undefined;
    }
    return formatEurFromCents(Math.max(0, next.revenueFromCents - this.monthlyRevenueCents()));
  });
}
